import { ENV } from "../../../../../utils/constants";

export const LIMIT_OPTIONS = [10, 20, 50];

export const MOVEMENT_TYPE_OPTIONS = [
  { value: "ENTRY", label: "Entrada" },
  { value: "EXIT", label: "Salida" },
  { value: "ADJUSTMENT_IN", label: "Ajuste (+)" },
  { value: "ADJUSTMENT_OUT", label: "Ajuste (−)" },
  { value: "INITIAL_STOCK", label: "Stock inicial" },
  { value: "SALE", label: "Venta" },
  { value: "SALE_CANCELLED", label: "Venta cancelada" },
  { value: "PHYSICAL_COUNT_IN", label: "Conteo físico (+)" },
  { value: "PHYSICAL_COUNT_OUT", label: "Conteo físico (−)" },
];

const MOVEMENT_TYPE_LABELS = Object.fromEntries(
  MOVEMENT_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

/*
 * Dirección de cada tipo de movimiento para mostrar la cantidad con
 * signo y color: 1 = suma stock, -1 = resta stock.
 */
const MOVEMENT_TYPE_DIRECTION = {
  ENTRY: 1,
  EXIT: -1,
  ADJUSTMENT_IN: 1,
  ADJUSTMENT_OUT: -1,
  INITIAL_STOCK: 1,
  SALE: -1,
  SALE_CANCELLED: 1,
  PHYSICAL_COUNT_IN: 1,
  PHYSICAL_COUNT_OUT: -1,
};

const MOVEMENT_SOURCE_LABELS = {
  MANUAL: "Manual",
  PRODUCT: "Producto",
  ORDER: "Pedido",
  PHYSICAL_COUNT: "Conteo físico",
};

export function getMovementTypeLabel(type) {
  return MOVEMENT_TYPE_LABELS[type] || type || "—";
}

export function getMovementSourceLabel(source) {
  return MOVEMENT_SOURCE_LABELS[source] || source || "—";
}

export function getMovementDirection(type) {
  if (type in MOVEMENT_TYPE_DIRECTION) {
    return MOVEMENT_TYPE_DIRECTION[type];
  }

  return 0;
}

export function getMovementBadgeClass(styles, type) {
  const direction = getMovementDirection(type);

  if (direction > 0) {
    return styles.PositiveBadge;
  }

  if (direction < 0) {
    return styles.NegativeBadge;
  }

  return styles.NeutralBadge;
}

export function getMovementsResponse(result) {
  const response = result?.movements || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response;
  }

  if (Array.isArray(response)) {
    return {
      docs: response,
      totalDocs: response.length,
      totalPages: 1,
      page: 1,
      limit: response.length,
    };
  }

  return {
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
  };
}

export function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const value = String(imagePath);

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  return `${ENV.SERVER_HOST}/${value
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")}`;
}

export function getProductImage(product) {
  const images = Array.isArray(product?.images) ? product.images : [];
  const coverImage = images.find((image) => image?.isCover) || images[0];

  return getImageUrl(coverImage?.path);
}

function getAttributeName(attribute) {
  if (attribute?.schema && typeof attribute.schema === "object") {
    return attribute.schema.name || "";
  }

  return attribute?.name || "";
}

function getAttributeValue(attribute) {
  if (
    attribute?.classification &&
    typeof attribute.classification === "object"
  ) {
    return (
      attribute.classification.name ||
      attribute.classification.value ||
      attribute.value ||
      ""
    );
  }

  return attribute?.label || attribute?.value || "";
}

export function getVariantLabel(variant) {
  const attributes = Array.isArray(variant?.attributes)
    ? variant.attributes
    : [];

  if (attributes.length === 0) {
    return "Variante única";
  }

  return attributes
    .map((attribute) => {
      const name = getAttributeName(attribute);
      const value = getAttributeValue(attribute);

      if (name && value) {
        return `${name}: ${value}`;
      }

      return value || name;
    })
    .filter(Boolean)
    .join(" · ");
}

export function getMovementVariant(movement) {
  const variants = Array.isArray(movement?.product?.variants)
    ? movement.product.variants
    : [];

  const variantId = String(movement?.variantId || "");

  return (
    variants.find((variant) => String(variant?._id) === variantId) || null
  );
}

export function getMovementUserName(movement) {
  const user = movement?.createdBy;

  if (!user) {
    return "—";
  }

  const fullName = `${user.firstname || ""} ${user.lastname || ""}`.trim();

  return fullName || user.email || "—";
}

export function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("es-GT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatSignedQuantity(movement) {
  const direction = getMovementDirection(movement?.type);
  const quantity = Number(movement?.quantity) || 0;
  const formatted = quantity.toLocaleString("es-GT");

  if (direction > 0) {
    return `+${formatted}`;
  }

  if (direction < 0) {
    return `−${formatted}`;
  }

  return formatted;
}

export function formatNumber(value) {
  return (Number(value) || 0).toLocaleString("es-GT");
}

export function formatCurrency(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return `Q ${(Number(value) || 0).toLocaleString("es-GT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function getProductFilterOptions(products = []) {
  if (!Array.isArray(products)) {
    return [];
  }

  return products
    .filter((product) => Boolean(product?._id))
    .map((product) => ({
      value: String(product._id),
      label: product.name || product.id || "Producto sin nombre",
    }));
}
